import { darken, getContrast, getLuminance, lighten, parseToHsl, setHue, tint } from 'polished';
import { isArray } from '~/utilities/type-guards';
import { reportError } from '~/utilities/errorReporting';
import { rgbFromCssColor, rgbFromHex } from './color-conversion';
import { luminanceFromRgb } from './contrast-ratio';
import { cleanHexValue } from './hex';

type ColorPaletteValue = {
  hexValue: string;
  relativeLuminance: number; // TODO - Constrict this as a decimal value between 0 and 1
};
type ColorPalette = ColorPaletteValue[];

export const colorContrastRatiosByShape = {
  nonTextHitAreaWithProperlyContrastingContents: 1, // https://www.w3.org/WAI/WCAG21/Understanding/non-text-contrast.html#intent
  nonText: 3, // 3:1 - https://www.w3.org/TR/WCAG21/#non-text-contrast
  largeText: 3, // 3:1 - https://www.w3.org/TR/WCAG21/#contrast-minimum
  paragraphText: 4.5, // 4.5:1 -  https://www.w3.org/TR/WCAG21/#contrast-minimum
  smallText: 5.5, // We're making this up, but it should be more than the paragraph text
} as const;

export const isDarkColor = (color: string): boolean => {
  const colorAsRgb = rgbFromCssColor(color);
  const relativeLuminance = luminanceFromRgb(...colorAsRgb);
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  return relativeLuminance < 0.15;
};

export const isVeryDarkColor = (color: string): boolean => {
  const colorAsRgb = rgbFromCssColor(color);
  const relativeLuminance = luminanceFromRgb(...colorAsRgb);
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  return relativeLuminance < 0.05;
};

export const isLightColor = (color: string): boolean => {
  const colorAsRgb = rgbFromCssColor(color);
  const relativeLuminance = luminanceFromRgb(...colorAsRgb);
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  return relativeLuminance >= 0.8;
};

export const isMidToneColor = (color: string): boolean => {
  const colorAsRgb = rgbFromCssColor(color);
  const relativeLuminance = luminanceFromRgb(...colorAsRgb);
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  return relativeLuminance > 0.13 && relativeLuminance < 0.75;
};

const getColorForColorPalette = (color: string): ColorPaletteValue => {
  const colorAsRgb = rgbFromHex(cleanHexValue(color));
  const luminance = luminanceFromRgb(...colorAsRgb);
  return {
    hexValue: color,
    relativeLuminance: luminance,
  };
};

export const generateColorPalette = (colors: string[]): ColorPalette => {
  // Make sure we have black and white colors available in case they're needed for sufficient contrast
  const uniqueColors = ['#ffffff', '#000000', ...colors].reduce<ColorPalette>((acc, color) => {
    if (acc.find(({ hexValue }) => hexValue === color)) {
      return acc;
    }
    acc.push(getColorForColorPalette(color));
    return acc;
  }, []);

  return uniqueColors.sort(
    (colorOne, colorTwo) => colorOne.relativeLuminance - colorTwo.relativeLuminance,
  );
};

export const getBestColorForText = (
  colorPalette: ColorPalette,
  colorsToExclude: string[] | string,
  backgroundColor: string,
  shape: keyof typeof colorContrastRatiosByShape = 'paragraphText',
): string => {
  // Always exclude the background color in addition to the colorsToExclude
  let excludedColors = isArray(colorsToExclude)
    ? [...colorsToExclude, backgroundColor]
    : [colorsToExclude, backgroundColor];

  // If one of the excluded colors is black or white, don't exclude it.
  // We always need black and white as potential text color options.
  excludedColors = excludedColors.filter(
    (color) =>
      color !== '#000' &&
      color !== '#000000' &&
      color.toLowerCase() !== '#fff' &&
      color.toLowerCase() !== '#ffffff',
  );

  const caseInsensitiveExcludedColors = excludedColors.map((color) => color.toLowerCase());

  const availableColors = colorPalette.filter((colorObj) => {
    return !caseInsensitiveExcludedColors.includes(colorObj.hexValue.toLowerCase());
  });

  if (availableColors.length === 1) {
    return availableColors[0].hexValue;
  }

  // If the background color is light, sort the available colors from lightest to darkest
  if (!isDarkColor(backgroundColor)) {
    availableColors.reverse();
  }

  const textColor = availableColors.find(({ hexValue }) => {
    const contrastRatio = getContrast(hexValue, backgroundColor);
    return contrastRatio >= colorContrastRatiosByShape[shape];
  });

  if (!textColor) {
    return isDarkColor(backgroundColor) ? '#ffffff' : '#000000';
  }

  return textColor.hexValue;
};

export const adjustColorForProperContrast = (
  foregroundColor: string,
  backgroundColor: string,
  shape: keyof typeof colorContrastRatiosByShape = 'paragraphText',
): string => {
  /* eslint-disable @typescript-eslint/no-magic-numbers */
  const initialContrastRatio = getContrast(foregroundColor, backgroundColor);
  if (initialContrastRatio >= colorContrastRatiosByShape[shape]) {
    return foregroundColor;
  }

  const { hue: foregroundColorHue, lightness: foregroundColorLightness } =
    parseToHsl(foregroundColor);

  // Does lightening the foreground color slightly increase the contrast ratio?
  let shouldForegroundColorBeLighter =
    foregroundColorLightness < 0.1 ||
    getContrast(tint(0.05, foregroundColor), backgroundColor) > initialContrastRatio;

  // If the foreground color is already very light and the initial contrast ratio was 1:1,
  // lightening the foreground color technically would have increased the contrast ratio, but
  // we know it can never be lightened enough to provide sufficient contrast,
  // so we'll set shouldForegroundColorBeLighter to false.
  if (foregroundColorLightness > 0.8 && initialContrastRatio === 1) {
    shouldForegroundColorBeLighter = false;
  }

  let adjustedForegroundColor = foregroundColor;
  let i = 0;
  while (
    getContrast(adjustedForegroundColor, backgroundColor) < colorContrastRatiosByShape[shape]
  ) {
    i += 1;
    // This is a safety mechanism to prevent getting stuck in an infinite loop.
    // If anyone exceeds 1,000 attempts to find a contrasting color, just return the best option we have
    // and log the colors where we couldn't generate sufficient contrast.
    if (i > 1000) {
      reportError(new Error(`Exceeded ${i} attempts to find contrasting color`), {
        key: 'adjust-color-for-proper-contrast',
        pillar: 'publish',
        product: 'customize',
        foregroundColor,
        backgroundColor,
      });
      return adjustedForegroundColor;
    }
    const adjustedForegroundColorLuminance = getLuminance(adjustedForegroundColor);
    // If the adjusted foreground color is white or black and the required contrast ratio is more than 3:1,
    // we should just return the adjusted foreground color.
    // Otherwise, if the adjusted foreground color is white or black,
    // reset the adjusted foreground color to the original foreground color and try looking
    // in the opposite direction for a properly contrasting color.
    if (
      i > 1 &&
      (adjustedForegroundColorLuminance === 1 || adjustedForegroundColorLuminance === 0)
    ) {
      if (colorContrastRatiosByShape[shape] > 3) {
        return getContrast('#fff', backgroundColor) > getContrast('#000', backgroundColor)
          ? '#fff'
          : '#000';
      }
      shouldForegroundColorBeLighter = !shouldForegroundColorBeLighter;
      adjustedForegroundColor = foregroundColor;
    }

    adjustedForegroundColor = shouldForegroundColorBeLighter
      ? setHue(foregroundColorHue, lighten(0.01, adjustedForegroundColor))
      : setHue(foregroundColorHue, darken(0.01, adjustedForegroundColor));
    /* eslint-enable @typescript-eslint/no-magic-numbers */
  }
  return adjustedForegroundColor;
};
